import {
  Box,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  useBreakpointValue,
  Flex,
  Text,
  SimpleGrid,
  VStack,
  HStack,
  Button,
  Icon,
  Heading,
  Tag,
  WrapItem,
  Wrap,
  Thead,
  Table,
  Th,
  Tr,
  Tbody,
  Td,
  Modal,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalOverlay,
  Input,
  Textarea,
  ModalFooter,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  CheckboxGroup,
  Stack,
  Checkbox,
  Container,
  Divider,
  Tooltip,
  IconButton,
  Badge,
  useToast,
} from "@chakra-ui/react";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { ChatDrawer } from "../../components/chat/ChatDrawer";
import { ChatNavbar } from "../../components/chat/ChatNavbar";
import { useNavigate } from "react-router-dom";
import React, { useEffect } from "react";
import { userAPI } from "services/user-api";
import { useChatStore } from "stores/chatStore";
import {
  ChatSessionType,
  TicketPriority,
  Character,
  AgentUsageMode,
  AIModel,
} from "types";
import {
  FaComments,
  FaUser,
  FaRobot,
  FaUsers,
  FaCreditCard,
  FaDollarSign,
  FaDiscord,
  FaUserSecret,
  FaCubes,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  FiCode,
  FiColumns,
  FiCopy,
  FiCreditCard,
  FiDollarSign,
  FiEdit,
  FiEye,
  FiFileText,
  FiGrid,
  FiLifeBuoy,
  FiList,
  FiMail,
  FiMessageSquare,
  FiPhoneCall,
  FiPlus,
  FiSettings,
  FiTrash,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { Agent } from "types";
import { agentsAPI } from "services/agents-api";
import { charactersAPI } from "services/characters-api";
import { aiAPI } from "services";
import { Footer } from "components/layout/Footer";

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export const Agents = () => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isOpenNewAgent, setIsOpenNewAgent] = React.useState<boolean>(false);
  const [credits, setCredits] = React.useState<number>(0);
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [usageMode, setUsageMode] = React.useState<AgentUsageMode>(
    AgentUsageMode.ANONYMOUS
  );

  const navigate = useNavigate();
  const {
    currentSession,
    sessions,
    loadSessions,
    createSession,
    deleteSession,
    updateSessionName,
  } = useChatStore();
  const { colorMode } = useColorMode();
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const [editingSessionId, setEditingSessionId] = React.useState<string | null>(
    null
  );
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [totalPages, setTotalPages] = React.useState(1);
  const [sessionNameInput, setSessionNameInput] = React.useState<string>("");
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const [newAgentName, setNewAgentName] = React.useState<string>("");
  const [newAgentDescription, setNewAgentDescription] =
    React.useState<string>("");
  const [newAgentModel, setNewAgentModel] = React.useState<string>("");
  const [newAgentTemperature, setNewAgentTemperature] =
    React.useState<number>(0.5);
  const [newAgentSystemPrompt, setNewAgentSystemPrompt] =
    React.useState<string>("");
  const [newAgentGreeting, setNewAgentGreeting] = React.useState<string>("");
  const [newAgentProfilePicture, setNewAgentProfilePicture] =
    React.useState<string>("");
  const [newAgentTools, setNewAgentTools] = React.useState<string[]>([]);
  const [characters, setCharacters] = React.useState<Character[]>([]);
  const [arariaModels, setArariaModels] = React.useState<AIModel[]>([]);
  const [apiKey, setApiKey] = React.useState<string>("");
  const toast = useToast();
  const loadAgents = async () => {
    try {
      const agents = await agentsAPI.getAgents(page, limit);
      setAgents(agents.agents);
      setTotalPages(agents.pagination.pages);
    } catch (error) {
      setError(t("agents.error"));
      console.error("Error loading agents:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const loadArariaModels = async () => {
    const arariaModels = await aiAPI.getModels();
    // filter models by capabilities (have to have chat and llm)
    const filteredModels = arariaModels.filter(
      (model) =>
        model.capabilities?.includes("chat") &&
        model.capabilities?.includes("tool-calling") &&
        (model.id?.includes("claude") ||
          model.id?.includes("gemini") ||
          model.id?.includes("gpt")) &&
        model.type === "llm"
    );
    setArariaModels(filteredModels);
  };
  const loadCharacters = async () => {
    try {
      const characters = await charactersAPI.getCharacters(page, limit);
      setCharacters(characters.characters);
    } catch (error) {
      setError(t("characters.error"));
      console.error("Error loading characters:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const loadUserData = async () => {
    try {
      const userData = await userAPI.getCurrentUser();
      setApiKey(userData.arariaApiKey);
      setCredits(Number(userData.credits));
    } catch (error) {
      console.error("Error loading user data:", error);
    }
  };
  useEffect(() => {
    loadAgents();
    loadCharacters();
    loadArariaModels();
    loadUserData();
  }, []);

  useEffect(() => {
    loadAgents();
  }, [page]);

  const handleSelectSession = async (selectedSessionId: string) => {
    await navigate(`/chat/${selectedSessionId}`);
  };
  const handleStartRename = (
    session: { id: string; sessionName?: string },
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setSessionNameInput(session.sessionName || "");
  };
  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingSessionId && sessionNameInput.trim()) {
      await updateSessionName(editingSessionId, sessionNameInput.trim());
      setEditingSessionId(null);
      setSessionNameInput("");
    }
  };
  const handleDeleteSession = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteSession(id);
  };
  const handleNewChat = async (
    sessionType: ChatSessionType = ChatSessionType.STANDARD
  ) => {
    const session = await createSession(sessionType);
    if (session) {
      navigate(`/chat/${session.id}`);
    }
    loadSessions();
  };

  const handleNewAgent = async () => {
    setIsLoading(true);
    const agent = await agentsAPI.createAgent({
      name: newAgentName,
      modelName: newAgentModel,
      temperature: newAgentTemperature,
      description: newAgentDescription,
      systemPrompt: newAgentSystemPrompt,
      greeting: newAgentGreeting,
      profilePicture: newAgentProfilePicture,
      tools: newAgentTools,
      usageMode: usageMode,
    });
    if (agent) {
      setIsOpenNewAgent(false);
      loadAgents();
    }
    setIsLoading(false);
  };
  const handleDeleteAgent = async (id: string) => {
    await agentsAPI.deleteAgent(id);
    loadAgents();
  };
  const handleCopyEmbedCode = (agent: Agent) => {
    const apiUrl =
      process.env.REACT_APP_ENV === "development"
        ? "http://localhost:3014"
        : "https://prod-api.araria.com.br";
    const fileName =
      process.env.REACT_APP_ENV === "development"
        ? "araria-chat.js"
        : "araria-chat.min.js";
    const cdn =
      process.env.REACT_APP_ENV === "development"
        ? "http://localhost/araria/araria-chat-widget"
        : "https://cdn.araria.com.br";
    const embedCode = `<!-- Araria Chat Widget -->
<div id="araria-chat-container"></div>
<script src="${cdn}/${fileName}"></script>
<script>
  ArariaChat.init({
    clientId: "${apiKey}",
    container: "araria-chat-container",
    theme: "dark", // Options: light, dark
    colorScheme: "blue", // Options: red, blue, green, orange, purple
    position: "bottom-right", // Options:bottom-right, bottom-left, top-right, top-left
    initialMessage: "How can I help you today?",
    apiUrl: "${apiUrl}",
    agentIds: ["${agent.id}"]
  });
</script>
<!-- End Araria Chat Widget -->
`;
    navigator.clipboard.writeText(embedCode);
    toast({
      title: t("agents.actions.embedCodeCopied"),
      description: t("agents.actions.embedCodeCopiedDescription"),
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };
  return (
    <Box h="100vh" display="flex" flexDirection="column">
      <ChatNavbar
        onOpen={onOpen}
        navigate={navigate}
        credits={credits}
        colorMode={colorMode}
        borderColor={borderColor}
      />
      <ChatDrawer
        isOpen={isOpen}
        onClose={onClose}
        sessions={sessions}
        handleNewChat={handleNewChat}
        handleSelectSession={handleSelectSession}
        handleRename={handleRename}
        handleDeleteSession={handleDeleteSession}
        editingSessionId={editingSessionId}
        sessionNameInput={sessionNameInput}
        setSessionNameInput={setSessionNameInput}
        useColorModeValue={useColorModeValue}
        setEditingSessionId={setEditingSessionId}
        handleStartRename={handleStartRename}
        currentSession={currentSession}
      />

      {/* <Box flex="1" bg={bgColor} py={20}> */}
      <Flex flex={1} p={8} align="center" justify="center" bg={bgColor} mt={10}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading
                size="2xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("agents.title")}
              </Heading>
              <Text color="gray.500">{t("agents.subtitle")}</Text>
            </VStack>

            {/* Tickets Section */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              <HStack w="full" justify="space-between">
                <Heading size="md">{t("agents.title")}</Heading>
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="brand"
                  onClick={() => setIsOpenNewAgent(true)}
                >
                  {t("agents.create")}
                </Button>
              </HStack>

              <Divider />

              {/* Tickets Table */}
              <Box overflowX="auto" w="100%">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>{t("agents.table.title")}</Th>
                      <Th>{t("agents.table.model")}</Th>
                      <Th>{t("agents.table.description")}</Th>
                      <Th>{t("agents.table.tools")}</Th>
                      <Th>{t("agents.table.actions")}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {agents.map((agent) => (
                      <Tr key={agent.id}>
                        <Td>
                          <VStack>
                            <Text fontWeight="bold">{agent.name}</Text>
                            <Badge colorScheme="gray" ml={2}>
                              {agent.usageMode}
                            </Badge>
                          </VStack>
                        </Td>
                        <Td>
                          <Tag size="sm" colorScheme="gray">
                            {agent.modelName}
                          </Tag>
                        </Td>
                        <Td>{agent.description}</Td>
                        <Td>
                          <Wrap>
                            {agent.tools.map((tool) => (
                              <WrapItem key={tool}>
                                <Tag size="sm" colorScheme="gray">
                                  {tool.includes("-")
                                    ? `${t("agents.character")}: ${
                                        characters.find(
                                          (character) => character.id === tool
                                        )?.name
                                      }`
                                    : tool}
                                </Tag>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </Td>

                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label={t("agents.actions.edit")}>
                              <IconButton
                                aria-label="Edit agent"
                                icon={<FiEdit />}
                                size="sm"
                                onClick={() => navigate(`/agents/${agent.id}`)}
                              />
                            </Tooltip>
                            <Tooltip label={t("agents.actions.delete")}>
                              <IconButton
                                aria-label="Delete agent"
                                icon={<FiTrash />}
                                size="sm"
                                onClick={() => handleDeleteAgent(agent.id)}
                              />
                            </Tooltip>
                            <Tooltip label={t("agents.actions.copyId")}>
                              <IconButton
                                aria-label="Copy agent id"
                                icon={<FiCopy />}
                                size="sm"
                                onClick={() =>
                                  navigator.clipboard.writeText(agent.id)
                                }
                              />
                            </Tooltip>
                            <Tooltip label={t("agents.actions.embed")}>
                              <IconButton
                                aria-label="Embed agent"
                                icon={<FiCode />}
                                size="sm"
                                onClick={() => handleCopyEmbedCode(agent)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {/* Add pagination controls */}
              <HStack spacing={2} justify="space-between" w="full">
                <Button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  isDisabled={page === 1}
                >
                  {t("common.previous")}
                </Button>
                <Text>
                  {t("common.page")} {page} / {totalPages}
                </Text>
                <Button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  isDisabled={page === totalPages}
                >
                  {t("common.next")}
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </Container>
        <Modal isOpen={isOpenNewAgent} onClose={() => setIsOpenNewAgent(false)}>
          <ModalOverlay />
          <ModalContent maxW="800px">
            <ModalHeader>{t("agents.newAgent")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box
                display={{ base: "block", md: "flex" }}
                flexDirection="row"
                gap={4}
              >
                <Box w={{ base: "full", md: "50%" }}>
                  <VStack spacing={4} align="flex-start">
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("agents.name")}
                      </Text>
                      <Input
                        type="text"
                        placeholder={t("agents.name")}
                        value={newAgentName}
                        onChange={(e) => setNewAgentName(e.target.value)}
                      />
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("agents.model")}
                      </Text>
                      <Select
                        placeholder={t("agents.model")}
                        value={newAgentModel}
                        onChange={(e) => setNewAgentModel(e.target.value)}
                      >
                        {arariaModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))}
                      </Select>
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("agents.temperature")}
                      </Text>
                      <NumberInput
                        value={newAgentTemperature}
                        min={0}
                        max={1}
                        step={0.1}
                        width="100%"
                        onChange={(valueString) => {
                          const value = parseFloat(valueString);
                          if (!isNaN(value) && value >= 0 && value <= 1) {
                            setNewAgentTemperature(value);
                          } else {
                            setNewAgentTemperature(0.5);
                          }
                        }}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("agents.description")}
                      </Text>
                      <Textarea
                        placeholder={t("agents.description")}
                        value={newAgentDescription}
                        onChange={(e) => setNewAgentDescription(e.target.value)}
                      />
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("agents.systemPrompt")}
                      </Text>
                      <Textarea
                        placeholder={t("agents.systemPrompt")}
                        value={newAgentSystemPrompt}
                        onChange={(e) =>
                          setNewAgentSystemPrompt(e.target.value)
                        }
                      />
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("agents.greeting")}
                      </Text>
                      <Textarea
                        placeholder={t("agents.greeting")}
                        value={newAgentGreeting}
                        onChange={(e) => setNewAgentGreeting(e.target.value)}
                      />
                    </Box>
                    <Box w="full">
                      <Text fontSize="sm" color="gray.500">
                        {t("agents.profilePicture")}
                      </Text>
                      <Input
                        type="text"
                        placeholder={t("agents.profilePicture")}
                        value={newAgentProfilePicture}
                        onChange={(e) =>
                          setNewAgentProfilePicture(e.target.value)
                        }
                      />
                    </Box>
                  </VStack>
                </Box>
                <Box w={{ base: "full", md: "50%" }}>
                  <VStack spacing={4} align="flex-start">
                    <Text fontSize="sm" color="gray.500">
                      {t("agents.tools")}
                    </Text>
                    {/* TODO: A list of check boxes with the tools */}
                    <CheckboxGroup
                      onChange={(value) => setNewAgentTools(value as string[])}
                    >
                      <Stack direction="column" spacing={2}>
                        <Checkbox value="SEARXNG">SearxNG</Checkbox>
                        <Checkbox value="GET_TODAYS_DATE">
                          Get Todays Date
                        </Checkbox>
                        <Checkbox value="SAVE_LEAD_TO_CSV">
                          Save Lead To CSV
                        </Checkbox>
                        <Checkbox value="CALCULATOR">Calculator</Checkbox>
                        <Checkbox value="IMAGE_GENERATION">
                          Image Generation
                        </Checkbox>
                        <Checkbox value="WEB_SCRAPER">Web Scraper</Checkbox>
                        <Checkbox value="GET_GEOCODE">Get Geocode</Checkbox>
                        <Checkbox value="GET_STREET_NAME_FROM_COORDINATES">
                          Get Street Name From Coordinates
                        </Checkbox>
                        {characters.map((character) => (
                          <Checkbox key={character.id} value={character.id}>
                            {t("agents.character")}: {character.name}
                          </Checkbox>
                        ))}
                      </Stack>
                    </CheckboxGroup>
                    <Text fontSize="sm" color="gray.500">
                      {t("agents.usageMode")}
                    </Text>
                    <Select
                      value={usageMode}
                      onChange={(e) =>
                        setUsageMode(e.target.value as AgentUsageMode)
                      }
                    >
                      <option value={AgentUsageMode.ANONYMOUS}>
                        {t("agents.anonymous")}
                      </option>
                      <option value={AgentUsageMode.AUTHENTICATED}>
                        {t("agents.authenticated")}
                      </option>
                    </Select>
                  </VStack>
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter>
              <Button onClick={handleNewAgent}>{t("agents.create")}</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Flex>
      <Footer />
    </Box>
  );
};
